/**
 * The tee sheet.
 *
 * This screen is a golf professional's morning, so it is drawn as the
 * paper sheet it replaces: one row per interval, top to bottom, with the
 * groups sitting in the slot they hold. Every other layout — a calendar
 * grid, a list of bookings — loses the thing the professional actually
 * reads off it, which is where the gaps are.
 *
 * Two things are deliberately visible rather than hidden behind a click:
 *
 *   - How many spaces are left in each slot. That is the number the
 *     person on the phone is being asked about.
 *   - Which players have already been charged. A group that went out
 *     without being checked in is money the club will not collect, and
 *     the only moment anyone can notice is while looking at the sheet.
 *
 * The booking rules are not re-implemented here. The form lets you try
 * anything the database would allow, and shows the refusal verbatim when
 * it would not — because the refusals are written to be read out loud to
 * the member standing at the desk, and paraphrasing them here would mean
 * two different explanations of the same rule.
 */

import { Fragment, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { date as fmtDate, money, titleCase, type CurrencyInfo } from '../lib/format.ts';
import { BylawNotices, Card, Empty, ErrorBanner, Loading, Status } from '../components/ui.tsx';
import { CourseConditions } from '../components/CourseConditions.tsx';
import { PersonLookup, type PersonHit } from '../components/Lookup.tsx';
import { clubToday, dayLabel, firstVisible, shiftDate, hhmm } from '../lib/sheet.ts';

interface Course {
  id: string;
  code: string;
  name: string;
  hole_count: number;
  nines: { nine_id: string; code: string; name: string; position: number }[];
  tees: {
    tee_set_id: string;
    tee_code: string;
    tee_name: string;
    rating_basis: string;
    course_rating: string;
    slope_rating: number;
    total_par: number;
    total_yards: number;
  }[];
}

interface Player {
  player_id: string;
  position: number;
  player_kind: string;
  person_id: string | null;
  name: string;
  sponsor_name: string | null;
  tee_name: string | null;
  riding: boolean;
  checked_in_at: string | null;
  charged: boolean;
}

interface Booking {
  booking_id: string;
  tee_time_id: string;
  status: string;
  holes: number;
  players: Player[];
}

interface Slot {
  tee_time_id: string;
  starts_at: string;
  slot_minutes: number;
  max_players: number;
  slot_status: string;
  slot_note: string | null;
  event_id: string | null;
  event_name: string | null;
  range_session_id: string | null;
  range_field_name: string | null;
  range_sport_name: string | null;
  range_starts_at: string | null;
  held_until: string | null;
  held_for_membership: string | null;
  day_type: string;
  players_booked: number;
  spaces_left: number;
  bookings: Booking[];
}

interface Sheet {
  course_id: string;
  date: string;
  slots: Slot[];
}


export function TeeSheetPage() {
  const { atLeast, currency, club } = useSession();
  const canOpenSheet = atLeast('manager');
  const zone = club?.timezone;

  const [date, setDate] = useState(() => clubToday(zone));
  const [courseId, setCourseId] = useState<string | null>(null);
  const [booking, setBooking] = useState<Slot | null>(null);
  const [note, setNote] = useState<string | null>(null);
  /**
   * Off: the sheet starts at the interval containing now. On: the whole
   * day, from the first tee time.
   *
   * Reset whenever the date changes, because "show me this morning" is
   * a thing somebody wants about a particular day and almost never about
   * the next one they page to.
   */
  const [wholeDay, setWholeDay] = useState(false);
  useEffect(() => setWholeDay(false), [date]);

  const courses = useApi<{ courses: Course[] }>('/v1/golf/courses');
  const course = courses.data?.courses.find((c) => c.id === courseId) ?? courses.data?.courses[0];

  const sheet = useApi<Sheet>(
    course ? `/v1/golf/tee-sheet?course_id=${course.id}&date=${date}` : null,
    [course?.id, date],
  );

  const openSheet = useMutation(async () => {
    const result = await api<{ total_slots: number }>('/v1/golf/tee-sheet/open', {
      method: 'POST',
      body: { course_id: course!.id, from_date: date, through_date: shiftDate(date, 6) },
    });
    setNote(
      result.total_slots === 0
        ? 'The sheet was already open for that week. Nothing changed.'
        : `Opened ${result.total_slots} tee times across the next seven days.`,
    );
    sheet.reload();
  });

  const slots = sheet.data?.slots ?? [];
  const booked = slots.reduce((a, s) => a + s.players_booked, 0);
  const capacity = slots
    .filter((s) => s.slot_status === 'open')
    .reduce((a, s) => a + s.max_players, 0);
  const uncharged = slots.flatMap((s) =>
    s.bookings.flatMap((b) =>
      b.players.filter((p) => p.checked_in_at && !p.charged && p.player_kind !== 'professional'),
    ),
  );

  // Where today's sheet starts. -1 means the whole day, which is what
  // every day but today gets.
  const cut = firstVisible(
    slots.map((s) => ({ startsAt: s.starts_at })),
    date,
    zone,
    wholeDay,
  );
  const from = cut < 0 ? 0 : cut;
  const visible = from > 0 ? slots.slice(from) : slots;

  return (
    <>
      <div className="row" style={{ marginBottom: 16 }}>
        <div>
          <h1>Tee Sheet</h1>
          <div className="muted small">
            {/* The weekday leads, because it is the fact the booking
                rules key off and a bare date does not carry it. */}
            <strong>{dayLabel(date, zone)}</strong> · {course ? course.name : 'the course'} ·{' '}
            {slots[0]?.day_type ?? '—'} · {booked} of {capacity} places taken
          </div>
        </div>
        <div className="spacer" />

        <select
          value={course?.id ?? ''}
          onChange={(e) => setCourseId(e.target.value)}
          style={{ maxWidth: 220 }}
        >
          {(courses.data?.courses ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        <button className="secondary small" onClick={() => setDate(shiftDate(date, -1))}>
          ‹
        </button>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          style={{ maxWidth: 170 }}
        />
        <button className="secondary small" onClick={() => setDate(shiftDate(date, 1))}>
          ›
        </button>

        {/* Only on today's sheet. On any other day there is no "now" to
            start from, so the checkbox would be a control that does
            nothing — which is worse than not being there. */}
        {date === clubToday(zone) && (
          <label className="sheet-toggle">
            <input
              type="checkbox"
              checked={wholeDay}
              onChange={(e) => setWholeDay(e.target.checked)}
            />
            <span>From the start of the day</span>
          </label>
        )}

        {canOpenSheet && (
          <button onClick={() => openSheet.run()} disabled={openSheet.busy || !course}>
            {openSheet.busy ? 'Opening…' : 'Open the week'}
          </button>
        )}
      </div>

      <ErrorBanner error={courses.error} />
      <ErrorBanner error={sheet.error} />
      <ErrorBanner error={openSheet.error} />

      {/* Tee-time priority, guest limits, whatever the club wrote down
          about who plays when. Shown at the sheet, where the argument
          actually happens. */}
      <BylawNotices surface="golf.booking" />
      <BylawNotices surface="guest" />

      {note && (
        <Card>
          <Status tone="good">{note}</Status>
        </Card>
      )}

      {uncharged.length > 0 && (
        <Card>
          <Status tone="warning">
            {uncharged.length} player(s) are checked in with nothing charged. A group that goes
            out unbilled is revenue the club does not get back.
          </Status>
        </Card>
      )}

      <Card flush>
        {sheet.loading ? (
          <div style={{ padding: 16 }}>
            <Loading rows={10} />
          </div>
        ) : slots.length === 0 ? (
          <Empty>
            The sheet is not open for {date}. {canOpenSheet
              ? 'Open the week to write its tee times.'
              : 'A manager has to open it first.'}
          </Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 80 }}>Time</th>
                <th>Group</th>
                <th style={{ width: 130 }}>Spaces</th>
                <th style={{ width: 200 }} />
              </tr>
            </thead>
            <tbody>
              {/* What is above the fold, said rather than hidden. A
                  sheet that quietly starts at two o'clock is a sheet
                  somebody thinks has lost the morning. */}
              {from > 0 && (
                <tr className="sheet-earlier">
                  <td colSpan={4}>
                    <button className="linkish" onClick={() => setWholeDay(true)}>
                      Show the {from} earlier tee time{from === 1 ? '' : 's'}
                    </button>
                  </td>
                </tr>
              )}
              {visible.map((s) => (
                <Fragment key={s.tee_time_id}>
                  <SlotRow
                    slot={s}
                    currency={currency}
                    booking={booking?.tee_time_id === s.tee_time_id}
                    onBook={() =>
                      // Pressing Book on the slot already being booked
                      // closes it. Otherwise the only way out of the form
                      // is Cancel, and the second press of a button that
                      // is already "on" doing nothing reads as a hang.
                      setBooking((open) => (open?.tee_time_id === s.tee_time_id ? null : s))
                    }
                    onChanged={(message) => {
                      setNote(message ?? null);
                      sheet.reload();
                    }}
                  />
                  {/* The form opens where the time is, not at the top of
                      the page. It used to render above the table, so
                      pressing Book on the 2:40 slot scrolled the sheet
                      away and left somebody filling in a form with no
                      idea which tee time it was for. */}
                  {booking?.tee_time_id === s.tee_time_id && course && (
                    <tr className="sheet-form">
                      <td colSpan={4}>
                        <BookingForm
                          slot={s}
                          course={course}
                          onDone={(message) => {
                            setBooking(null);
                            setNote(message);
                            sheet.reload();
                          }}
                          onCancel={() => setBooking(null)}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------ */

function SlotRow({
  slot,
  currency,
  booking,
  onBook,
  onChanged,
}: {
  slot: Slot;
  currency: CurrencyInfo;
  /** True while this row's booking form is the one that is open. */
  booking: boolean;
  onBook: () => void;
  onChanged: (message?: string) => void;
}) {
  const { atLeast } = useSession();
  // Three ways a slot is not an ordinary one, and they are not the same
  // thing. Blocked is the professional's frost or aeration. Event means
  // the club gave the morning away and the field is entered against the
  // event, not booked onto the sheet. Held is the professional keeping a
  // slot back — still bookable, but only deliberately.
  const blocked = slot.slot_status === 'blocked';
  const owned = slot.slot_status === 'event';
  const held = slot.slot_status === 'held';
  // Two different "held". slot_status 'held' is the professional keeping
  // a slot back for the week. beingBooked is somebody filling in a form
  // right now, and lasts about ninety seconds.
  const beingBooked = Boolean(slot.held_until) && new Date(slot.held_until!) > new Date();
  // Blocked by the shooting range rather than by the professional. Same
  // status, different owner, and the difference matters twice: the
  // reason is worth naming, and unblocking it here would leave the range
  // holding a slot it thinks is closed.
  const rangeHolds = Boolean(slot.range_session_id);
  const bookable = !blocked && !owned;

  const checkIn = useMutation(async (bookingId: string) => {
    const result = await api<{
      total_posted_minor: number;
      needs_attention: { player_name: string; not_posted_reason: string }[] | null;
    }>(`/v1/golf/bookings/${bookingId}/check-in`, { method: 'POST' });

    onChanged(
      result.needs_attention
        ? `Checked in, but ${result.needs_attention
            .map((n) => `${n.player_name}: ${n.not_posted_reason}`)
            .join('; ')}`
        : result.total_posted_minor > 0
          ? `Checked in and charged ${money(result.total_posted_minor, currency)}.`
          : 'Checked in. Nothing to charge — they play on their dues.',
    );
  });

  const block = useMutation(async () => {
    await api(`/v1/golf/tee-times/${slot.tee_time_id}`, {
      method: 'PATCH',
      body: blocked ? { status: 'open', note: null } : { status: 'blocked', note: 'blocked' },
    });
    onChanged();
  });

  return (
    <>
      <tr style={blocked || owned ? { opacity: 0.55 } : undefined}>
        <td className="mono">{hhmm(slot.starts_at)}</td>
        <td>
          {blocked ? (
            rangeHolds ? (
              // Name the session. "Blocked" sends the person at the desk
              // looking for whoever blocked it; "the skeet field is hot
              // from 10:00" ends the conversation.
              <Status tone="serious">
                {slot.range_sport_name ?? 'The range'} — {slot.range_field_name ?? 'a field'} is
                hot{slot.range_starts_at ? ` from ${hhmm(slot.range_starts_at)}` : ''}
              </Status>
            ) : (
              <Status tone="serious">Blocked{slot.slot_note ? ` — ${slot.slot_note}` : ''}</Status>
            )
          ) : owned ? (
            // Name the event. "This slot is an event" sends the person at
            // the desk looking; "the Member-Guest has that morning" ends
            // the conversation.
            <Status tone="neutral">{slot.event_name ?? 'An event'}</Status>
          ) : beingBooked && slot.bookings.length === 0 ? (
            <Status tone="warning">Somebody is booking this</Status>
          ) : slot.bookings.length === 0 ? (
            <span className="muted small">—</span>
          ) : (
            slot.bookings.map((b) => (
              <div key={b.booking_id} style={{ marginBottom: 4 }}>
                {b.players.map((p) => (
                  <span
                    key={p.player_id}
                    style={{
                      display: 'inline-block',
                      marginRight: 10,
                      textDecoration: b.status === 'canceled' ? 'line-through' : undefined,
                    }}
                  >
                    {p.name}
                    {p.player_kind === 'member_guest' && (
                      <span className="muted small">
                        {' '}
                        (guest of {p.sponsor_name ?? 'a member'})
                      </span>
                    )}
                    {p.player_kind === 'outside' && (
                      <span className="muted small"> (outside play)</span>
                    )}
                    {!p.riding && <span className="muted small"> · walking</span>}
                    {p.charged && <span className="muted small"> · charged</span>}
                  </span>
                ))}
                {b.status === 'checked_in' && (
                  <span className="muted small"> · {b.holes} holes · checked in</span>
                )}
              </div>
            ))
          )}
        </td>
        <td>
          {blocked || owned ? (
            <span className="muted small">—</span>
          ) : slot.spaces_left === 0 ? (
            <Status tone="neutral">Full</Status>
          ) : (
            <Status tone="good">
              {slot.spaces_left} of {slot.max_players}
            </Status>
          )}
        </td>
        <td className="actions">
          {bookable && slot.spaces_left > 0 && !beingBooked && (
            <button
              className={booking ? 'small' : 'secondary small'}
              onClick={onBook}
              aria-expanded={booking}
            >
              {booking ? 'Close' : held ? 'Book (held)' : 'Book'}
            </button>
          )}
          {slot.bookings
            .filter((b) => b.status === 'booked')
            .map((b) => (
              <button
                key={b.booking_id}
                className="small"
                disabled={checkIn.busy}
                onClick={() => checkIn.run(b.booking_id)}
              >
                Check in
              </button>
            ))}
          {/* Not offered on a slot an event owns. Blocking it would
              overwrite the status the event is holding, and releasing the
              event afterwards would put back whatever the slot was before
              the event took it — quietly losing the block. Hand the sheet
              back on the Events tab first. */}
          {/* Nor on a slot the shooting range is holding. Unblocking it
              would leave the range's own ledger saying the course is shut
              for a session it is still running, and the range would put
              the block back the moment anybody touched it. Hand the
              morning back on the range sheet instead. */}
          {atLeast('manager') && !owned && !rangeHolds && slot.bookings.length === 0 && (
            <button className="secondary small" disabled={block.busy} onClick={() => block.run()}>
              {blocked ? 'Unblock' : 'Block'}
            </button>
          )}
        </td>
      </tr>
      {(checkIn.error || block.error) && (
        <tr>
          <td colSpan={4}>
            <ErrorBanner error={checkIn.error ?? block.error} />
          </td>
        </tr>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */

interface Draft {
  player_kind: 'member' | 'member_guest' | 'outside';
  /**
   * The person, as chosen — not as typed.
   *
   * This field used to be a `person_id: string` behind a text box asking
   * for a uuid. Nothing in this product shows anybody a uuid, so the
   * field could not be filled in and the tee sheet could not book a tee
   * time. Holding the whole hit rather than the id means the form can
   * show WHO was picked, and can fill the membership in from the first
   * member chosen instead of asking twice.
   */
  person: PersonHit | null;
  display_name: string;
  sponsor: PersonHit | null;
  riding: boolean;
}

const emptyPlayer: Draft = {
  player_kind: 'member',
  person: null,
  display_name: '',
  sponsor: null,
  riding: true,
};

function BookingForm({
  slot,
  course,
  onDone,
  onCancel,
}: {
  slot: Slot;
  course: Course;
  onDone: (message: string) => void;
  onCancel: () => void;
}) {
  const [holes, setHoles] = useState<9 | 18>(18);
  const [players, setPlayers] = useState<Draft[]>([{ ...emptyPlayer }]);
  const [taken, setTaken] = useState<string | null>(null);

  /**
   * Whose booking this is.
   *
   * Taken from the first member in the group rather than asked for. The
   * form used to have a "membership id" box above the players, which
   * meant answering the same question twice — once as a uuid nobody has,
   * and again by naming the players. The booking window and the weekend
   * rules key off the membership's category, and the membership of the
   * first member playing is that membership in every case anybody has
   * described. Shown below, so it is visible rather than assumed.
   */
  const owner = players.find((p) => p.player_kind === 'member' && p.person)?.person ?? null;
  const membershipId = owner?.membership_id ?? '';

  /**
   * Hold the slot while this form is open.
   *
   * The database now refuses a second booking that would over-fill a slot
   * (GOLF-4, migration 0111), which is correct and, on its own, unkind:
   * the second member finds out after typing four names. The hold moves
   * that answer to before they start.
   *
   * Re-taken every half-window because the form outlives one hold — a
   * member looking up a guest's surname takes longer than ninety seconds
   * more often than anybody expects. Asking again for a hold you own
   * extends it, so this is one call and not a special "renew" path.
   *
   * The hold follows the membership, so it is re-taken when the member
   * being booked for changes. Without that, choosing the member after
   * opening the form leaves the hold anonymous and it protects nobody.
   */
  /**
   * The membership the hold is taken for, read by the effect below
   * without being one of its dependencies.
   *
   * This used to be a dependency, and the membership used to be typed
   * into a box — so it changed once, when somebody finished typing. Now
   * it is derived from the first member chosen, so it changes the
   * instant a name is picked, and the effect's cleanup fired: DELETE the
   * hold, then POST for a new one. The two crossed on the wire and the
   * POST came back 422 — the form said "this tee time is not held" the
   * moment you told it who was playing, which is precisely backwards.
   */
  const holdFor = useRef(membershipId);
  holdFor.current = membershipId;

  useEffect(() => {
    let alive = true;

    const take = async () => {
      try {
        const r = await api<{ expires_at: string; seconds: number }>(
          `/v1/golf/tee-times/${slot.tee_time_id}/hold`,
          { method: 'POST', body: { membership_id: holdFor.current || undefined } },
        );
        if (alive) setTaken(r.expires_at);
      } catch {
        // Losing the hold is not a reason to close a form somebody is
        // typing into. The booking itself is still guarded, and it will
        // say so plainly if the slot has gone.
        if (alive) setTaken(null);
      }
    };

    void take();
    const timer = setInterval(take, 40_000);

    return () => {
      alive = false;
      clearInterval(timer);
      // Give it straight back. A member who changes their mind should not
      // cost the next member ninety seconds. A successful booking clears
      // its own hold in the database, so this is a no-op after one.
      void api(`/v1/golf/tee-times/${slot.tee_time_id}/hold`, { method: 'DELETE' })
        .catch(() => {});
    };
    // The SLOT, and nothing else. The hold belongs to the tee time, not
    // to whoever is currently named in the form.
  }, [slot.tee_time_id]);

  /**
   * Re-take the hold once the membership is known, so the database
   * records who it is being held for. Re-taking a hold you already own
   * extends it, so this is the same call rather than a special path —
   * and crucially it does NOT release anything first.
   */
  useEffect(() => {
    if (!membershipId) return;
    let alive = true;
    void api<{ expires_at: string }>(`/v1/golf/tee-times/${slot.tee_time_id}/hold`, {
      method: 'POST',
      body: { membership_id: membershipId },
    })
      .then((r) => {
        if (alive) setTaken(r.expires_at);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [slot.tee_time_id, membershipId]);

  const book = useMutation(async () => {
    await api('/v1/golf/bookings', {
      method: 'POST',
      body: {
        tee_time_id: slot.tee_time_id,
        membership_id: membershipId || null,
        holes,
        players: players.map((p) => ({
          player_kind: p.player_kind,
          person_id: p.person?.person_id ?? null,
          display_name: p.display_name || null,
          sponsor_person_id: p.sponsor?.person_id ?? null,
          riding: p.riding,
        })),
      },
    });
    onDone(`Booked ${players.length} player(s) at ${hhmm(slot.starts_at)}.`);
  });

  const set = (i: number, patch: Partial<Draft>) =>
    setPlayers((list) => list.map((p, n) => (n === i ? { ...p, ...patch } : p)));

  /**
   * Every player is answered for.
   *
   * The button used to be pressable with the form blank, which sent an
   * empty booking to the database and came back with a constraint
   * violation — a refusal that reads as the software being broken rather
   * than as the form being incomplete. Checked here so the button says
   * so first, and still checked there, because a disabled button is a
   * courtesy and never a rule.
   */
  const ready = players.every((p) =>
    p.player_kind === 'member'
      ? p.person !== null
      : p.display_name.trim() !== '' &&
        (p.player_kind !== 'member_guest' || p.sponsor !== null),
  );

  return (
    <Card title={`Book ${hhmm(slot.starts_at)} — ${course.name}`}>
      <ErrorBanner error={book.error} />

      {/* Said plainly, because the alternative is a member wondering
          whether they are racing somebody. If the hold could not be taken
          they are — and they should know before they type four names. */}
      <div className="hint" style={{ marginBottom: 10 }}>
        {taken
          ? 'This tee time is held for you while you fill this in.'
          : 'This tee time is not held — somebody else may be booking it too.'}
      </div>

      <div className="grid-2">
        <label className="field">
          <span>Holes</span>
          <select value={holes} onChange={(e) => setHoles(Number(e.target.value) as 9 | 18)}>
            <option value={18}>18</option>
            <option value={9}>9</option>
          </select>
        </label>
        <div className="field-block">
          <span className="field-label">Booked against</span>
          <div className="hint">
            {owner
              ? `${owner.account_name ?? owner.label}${
                  owner.membership_number ? ` · ${owner.membership_number}` : ''
                } — how far ahead this booking may be made, and how early it may tee off at a weekend, are that category's rules.`
              : 'The first member added below. Guests and outside play are charged to whoever is answerable for them.'}
          </div>
        </div>
      </div>

      {players.map((p, i) => (
        <div key={i} className="grid-2" style={{ marginTop: 8 }}>
          <label className="field">
            <span>Player {i + 1}</span>
            <select
              value={p.player_kind}
              onChange={(e) =>
                // Changing what kind of player this is clears whoever was
                // chosen. A member turned into a guest kept the member's
                // person_id AND gained a typed name, and the booking went
                // in with both.
                set(i, {
                  player_kind: e.target.value as Draft['player_kind'],
                  person: null,
                  sponsor: null,
                  display_name: '',
                })
              }
            >
              <option value="member">Member</option>
              <option value="member_guest">Member's guest</option>
              <option value="outside">Outside play</option>
            </select>
          </label>
          {p.player_kind === 'member' ? (
            <PersonLookup
              label="Who is playing"
              value={p.person}
              onChange={(person) => set(i, { person })}
            />
          ) : (
            <label className="field">
              <span>Name</span>
              <input
                value={p.display_name}
                onChange={(e) => set(i, { display_name: e.target.value })}
                placeholder="the guest's name"
              />
            </label>
          )}
          {p.player_kind === 'member_guest' && (
            <PersonLookup
              label="Sponsored by"
              hint="The member answerable for them. The guest fee lands on that member's account."
              value={p.sponsor}
              onChange={(sponsor) => set(i, { sponsor })}
              filter={(hit) => hit.may_charge !== false && hit.member_account_id !== null}
            />
          )}
          {/* Full width, not a third cell in a two-column grid. The
              grid puts an odd third child in a half-width column, and
              beside a lookup that is now three lines tall the words
              "Taking a cart" wrapped to two and sat level with nothing.
              A checkbox is one line; give it the row. */}
          <label
            className="muted small"
            style={{
              gridColumn: '1 / -1',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <input
              type="checkbox"
              checked={p.riding}
              onChange={(e) => set(i, { riding: e.target.checked })}
            />
            Taking a cart
          </label>
        </div>
      ))}

      <div className="actions" style={{ marginTop: 12 }}>
        {players.length < slot.spaces_left && (
          <button
            className="secondary small"
            onClick={() => setPlayers((l) => [...l, { ...emptyPlayer }])}
          >
            Add a player
          </button>
        )}
        <div className="spacer" />
        <button className="secondary" onClick={onCancel}>
          Cancel
        </button>
        <button onClick={() => book.run()} disabled={book.busy || !ready}>
          {book.busy ? 'Booking…' : 'Book'}
        </button>
      </div>

      <div className="muted small" style={{ marginTop: 10, lineHeight: 1.6 }}>
        A guest needs a member answerable for them — that member's account is where the guest fee
        lands. The club sets its own booking rules once, per category, and this form does not
        override them: it lets you try anything the database would allow and shows the refusal
        verbatim when it would not.
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ */

/**
 * The scorecard.
 *
 * Its own screen rather than a panel on the tee sheet, because the thing
 * it is for — checking that the card the software prints matches the card
 * on the first tee — is a job somebody does once a season with the paper
 * in their hand.
 */
/* ------------------------------------------------------------------ */
/* The pin sheet                                                       */
/*                                                                     */
/* The most-looked-at piece of paper at a golf club, which until now   */
/* the software has not held. Zones rather than paces — migration 0081 */
/* argues that at length; the short of it is that a club which cannot  */
/* measure eighteen greens in the dark every morning ends up with a    */
/* table of yesterday's numbers, and a stale measurement is believed   */
/* where a missing one is not.                                         */
/* ------------------------------------------------------------------ */

interface Pin {
  playing_number: number;
  hole_id: string;
  nine_name: string;
  par: number;
  stroke_index: number;
  pin_id: string | null;
  depth: string | null;
  width: string | null;
  difficulty: string | null;
  note: string | null;
  set_by_name: string | null;
  said_aloud: string | null;
}

const DEPTHS = ['front', 'middle', 'back'] as const;
const WIDTHS = ['left', 'center', 'right'] as const;

function PinSheet({ courseId }: { courseId: string }) {
  const { atLeast, club } = useSession();
  const canSet = atLeast('staff');
  const [date, setDate] = useState(() => clubToday(club?.timezone));
  const q = useApi<{
    play_date: string; is_today: boolean; set_count: number;
    rotate_from: string | null; holes: Pin[];
  }>(`/v1/golf/pins?course_id=${courseId}&date=${date}`, [courseId, date]);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, { depth: string; width: string }>>({});
  const [note, setNote] = useState<string | null>(null);

  const save = useMutation(async () => {
    const pins = Object.entries(draft).map(([hole_id, v]) => ({ hole_id, ...v }));
    if (pins.length === 0) { setEditing(false); return; }
    const r = await api<{ set_count: number; unset_count: number }>('/v1/golf/pins', {
      method: 'PUT', body: { course_id: courseId, play_date: date, pins },
    });
    setEditing(false);
    setDraft({});
    // How many greens are still blank. The crew sets the front nine, gets
    // called away, and this is the number that tells them what is left.
    setNote(r.unset_count > 0
      ? `Saved. ${r.unset_count} green(s) still without a pin.`
      : 'Saved. All eighteen set.');
    q.reload();
  });

  const rotate = useMutation(async () => {
    const r = await api<{ pins_created: number; note: string }>('/v1/golf/pins/rotate', {
      method: 'POST', body: { course_id: courseId, play_date: date },
    });
    setNote(`${r.pins_created} pin(s) moved on. ${r.note}`);
    q.reload();
  });

  const holes = q.data?.holes ?? [];
  const value = (h: Pin, k: 'depth' | 'width') =>
    draft[h.hole_id]?.[k] ?? (k === 'depth' ? h.depth : h.width) ?? '';

  const set = (h: Pin, k: 'depth' | 'width', v: string) =>
    setDraft((d) => ({
      ...d,
      [h.hole_id]: {
        depth: k === 'depth' ? v : (d[h.hole_id]?.depth ?? h.depth ?? 'middle'),
        width: k === 'width' ? v : (d[h.hole_id]?.width ?? h.width ?? 'center'),
      },
    }));

  return (
    <Card
      title="Pin Positions"
      actions={
        <div className="actions">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          {canSet && !editing && (
            <>
              {q.data?.rotate_from && (
                <button className="secondary small" disabled={rotate.busy}
                  onClick={() => rotate.run()}>
                  Rotate from {fmtDate(q.data.rotate_from)}
                </button>
              )}
              <button className="gold small" onClick={() => setEditing(true)}>
                {q.data?.set_count ? 'Correct the sheet' : 'Set the sheet'}
              </button>
            </>
          )}
          {editing && (
            <>
              <button className="gold small" disabled={save.busy} onClick={() => save.run()}>
                {save.busy ? 'Saving…' : 'Save'}
              </button>
              <button className="secondary small"
                onClick={() => { setEditing(false); setDraft({}); }}>
                Cancel
              </button>
            </>
          )}
        </div>
      }
    >
      {note && <div className="notice">{note}</div>}
      <ErrorBanner error={q.error ?? save.error ?? rotate.error} />

      {q.loading ? <Loading /> : (
        <>
          <p className="muted small">
            {q.data?.set_count === 0
              ? 'Nothing cut for this day yet.'
              : `${q.data?.set_count} of ${holes.length} greens set.`}
            {' '}Front, middle or back by left, center or right — what the crew writes down,
            and what a caddie says on the tee.
          </p>

          <table className="data">
            <thead>
              <tr>
                <th className="num">Hole</th><th>Nine</th>
                <th className="num">Par</th><th className="num">SI</th>
                <th>Depth</th><th>Width</th><th>Today</th><th>Set by</th>
              </tr>
            </thead>
            <tbody>
              {holes.map((h) => (
                <tr key={h.hole_id}>
                  <td className="num"><strong>{h.playing_number}</strong></td>
                  <td className="muted small">{h.nine_name}</td>
                  <td className="num">{h.par}</td>
                  <td className="num">{h.stroke_index}</td>
                  <td>
                    {editing ? (
                      <select value={value(h, 'depth')}
                        onChange={(e) => set(h, 'depth', e.target.value)}>
                        <option value="">—</option>
                        {DEPTHS.map((d) => <option key={d} value={d}>{titleCase(d)}</option>)}
                      </select>
                    ) : (h.depth ? titleCase(h.depth) : <span className="muted">—</span>)}
                  </td>
                  <td>
                    {editing ? (
                      <select value={value(h, 'width')}
                        onChange={(e) => set(h, 'width', e.target.value)}>
                        <option value="">—</option>
                        {WIDTHS.map((w) => <option key={w} value={w}>{titleCase(w)}</option>)}
                      </select>
                    ) : (h.width ? titleCase(h.width) : <span className="muted">—</span>)}
                  </td>
                  <td>
                    {/* The sentence, assembled server-side so the member
                        app and this screen cannot word it differently. */}
                    {h.said_aloud
                      ? <strong>{h.said_aloud}</strong>
                      : <span className="muted small">not cut</span>}
                    {h.difficulty && <div className="hint">{titleCase(h.difficulty)}</div>}
                    {h.note && <div className="hint">{h.note}</div>}
                  </td>
                  <td className="muted small">{h.set_by_name ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Card>
  );
}

export function ScorecardPage() {
  const courses = useApi<{ courses: Course[] }>('/v1/golf/courses');
  const [courseId, setCourseId] = useState<string | null>(null);
  const course = courses.data?.courses.find((c) => c.id === courseId) ?? courses.data?.courses[0];

  const card = useApi<{
    course: { name: string };
    holes: {
      playing_number: number;
      stroke_index: number;
      nine_name: string;
      hole_name: string | null;
      par: number;
      yards_by_tee: Record<string, number>;
    }[];
    tees: {
      tee_set_id: string;
      tee_code: string;
      tee_name: string;
      rating_basis: string;
      course_rating: string;
      slope_rating: number;
      total_par: number;
      total_yards: number;
    }[];
  }>(course ? `/v1/golf/courses/${course.id}/card` : null, [course?.id]);

  const teeCodes = [...new Set((card.data?.tees ?? []).map((t) => t.tee_code))];

  return (
    <>
      <div className="row" style={{ marginBottom: 16 }}>
        <div>
          <h1>Course Information</h1>
          <div className="muted small">
            Par, stroke index, yardage, and where the holes are cut today. A nine that plays in
            two eighteens is the same length in both and carries a different stroke index in each.
          </div>
        </div>
        <div className="spacer" />
        <select value={course?.id ?? ''} onChange={(e) => setCourseId(e.target.value)}>
          {(courses.data?.courses ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {/* Above the pin sheet, because a hole that is closed outranks
          where the flag is on it. */}
      {course && <CourseConditions courseId={course.id} />}

      {course && <PinSheet courseId={course.id} />}

      <ErrorBanner error={card.error} />

      <Card flush>
        {card.loading ? (
          <div style={{ padding: 16 }}>
            <Loading rows={10} />
          </div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 50 }}>Hole</th>
                <th>Nine</th>
                <th className="num">Par</th>
                <th className="num">Index</th>
                {teeCodes.map((t) => (
                  <th key={t} className="num">
                    {t}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(card.data?.holes ?? []).map((h) => (
                <tr key={h.playing_number}>
                  <td className="mono">{h.playing_number}</td>
                  <td>
                    {h.nine_name}
                    {h.hole_name && <span className="muted small"> · {h.hole_name}</span>}
                  </td>
                  <td className="num">{h.par}</td>
                  <td className="num">{h.stroke_index}</td>
                  {teeCodes.map((t) => (
                    <td key={t} className="num mono">
                      {h.yards_by_tee?.[t] ?? '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Ratings">
        <table className="data">
          <thead>
            <tr>
              <th>Tee</th>
              <th>Rated for</th>
              <th className="num">Rating</th>
              <th className="num">Slope</th>
              <th className="num">Par</th>
              <th className="num">Yards</th>
            </tr>
          </thead>
          <tbody>
            {(card.data?.tees ?? []).map((t) => (
              <tr key={`${t.tee_set_id}-${t.rating_basis}`}>
                <td>{t.tee_name}</td>
                <td className="muted">{t.rating_basis}</td>
                <td className="num mono">{t.course_rating}</td>
                <td className="num mono">{t.slope_rating}</td>
                <td className="num mono">{t.total_par}</td>
                <td className="num mono">{t.total_yards}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="muted small" style={{ marginTop: 10, lineHeight: 1.6 }}>
          Rating and slope come from the state association after a rating visit. They are the only
          two numbers a course handicap is computed from, and they cannot be worked out from the
          yardage — so they are entered from the certificate, not derived.
        </div>
      </Card>
    </>
  );
}
